import type { IReview } from "@api/models/IReview";
import AppThemeIcon from "@core-components/icon/AppThemeIcon";
import Icon from "@core-components/icon/Icon";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Group, Text } from "@mantine/core";
import AppSkeleton from "@modules/loader/AppSkeleton";
import clsx from "clsx";
import { type ForwardedRef, forwardRef } from "react";
import { Link } from "react-router";
import CtaReview from "./CtaReview";
import Grade from "./Grade";
import ReviewContainer from "./ReviewContainer";

interface ReviewProps {
  review: IReview;
}

const Review = forwardRef(
  ({ review }: ReviewProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { t } = useAppTranslation("pages.reviews");

    if (review.type === "cta") {
      return <CtaReview ref={ref} />;
    }

    return (
      <Link
        to={review.url}
        target="_blank"
        className={clsx("no-underline", {
          "pointer-events-none": review.type === "skeleton",
        })}
      >
        <ReviewContainer ref={ref}>
          <Group justify="space-between">
            <AppSkeleton>
              <Text className="font-secret-black">{review.author}</Text>
            </AppSkeleton>
            <AppSkeleton>
              <Grade author={review.author} grade={review.grade} />
            </AppSkeleton>
          </Group>
          <AppSkeleton>
            <Text>{review.comment}</Text>
          </AppSkeleton>
          <Group justify="space-between">
            <AppSkeleton>
              <Group gap={"xs"}>
                <AppThemeIcon className="size-8">
                  <Icon.TrustpilotStartFilled />
                </AppThemeIcon>
                <Text className="pt-1">{t("trustpilot")}</Text>
              </Group>
            </AppSkeleton>
            <AppSkeleton>
              <Text>{t("reviewDate", { date: review.date.toDate() })}</Text>
            </AppSkeleton>
          </Group>
        </ReviewContainer>
      </Link>
    );
  },
);

export default Review;
